import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import ReactSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Loading, Modal, RadioButton } from '@wfp/ui';

import { useDocId } from '../../hooks/home';
import { getTopics } from '../../restapi/document';
import { type CreateSectionData, type SimpleOption, Topic, topicLabels, type TopicTitle } from '../../restapi/types';
import { prepareCreateSectionData } from '../../restapi/section';


interface Props {
	disableHighlights: boolean;
	done: (data?: CreateSectionData) => void,
}

const directTopics = [
	// {id: Topic.redAlert, label: topicLabels[Topic.redAlert]},
	// {id: Topic.orangeAlert, label: topicLabels[Topic.orangeAlert]},
	// {id: Topic.yellowAlert, label: topicLabels[Topic.yellowAlert]},
	{id: Topic.other, label: topicLabels[Topic.other]},
	{id: Topic.naturalHazard, label: topicLabels[Topic.naturalHazard]},
];

export enum CreateSectionType {
	emergency = 'emergency',
	topic = 'topic',
	highlights = 'highlights',
}

export function AddSectionModal(props: Props) {
	const docId = useDocId();
	const emergencyTopics = useQuery(['topic', docId], () => getTopics(docId));

	const [sectionType, setSectionType] = useState<CreateSectionType>(CreateSectionType.emergency);
	const [emergencyId, setEmergencyId] = useState<number | undefined>();
	const [directTopicId, setDirectTopicId] = useState<Topic | undefined>();
	const [directTopicName, setDirectTopicName] = useState<string>('');
	const directTopicNameRef = useRef<HTMLSelectElement>(null);
	const [directTopicKnownNames, setDirectTopicKnownNames] = useState<SimpleOption[]>([]);

	useEffect(() => {
		sectionType !== CreateSectionType.emergency && setEmergencyId(undefined);
		sectionType !== CreateSectionType.topic && setDirectTopicId(undefined);
		sectionType !== CreateSectionType.topic && setDirectTopicName('');
	}, [sectionType]);

	useEffect(() => {
		emergencyId && setSectionType(CreateSectionType.emergency);
	}, [emergencyId]);

	useEffect(() => {
		if (directTopicId) {
			setSectionType(CreateSectionType.topic);
			// setFocusOnDirectTopicName();

			const topicInfo = emergencyTopics?.data?.others.find(t => t.topic === directTopicId);
			setDirectTopicKnownNames((topicInfo?.feCleanTitles || []).map(t => ({value: t, label: t})));

			directTopicNameRef.current?.focus();
		}
	}, [directTopicId, directTopicNameRef]); // eslint-disable-line react-hooks/exhaustive-deps

	const canCreate = (sectionType === CreateSectionType.highlights) ||
		((sectionType === CreateSectionType.emergency) && emergencyId) ||
		((sectionType === CreateSectionType.topic) && directTopicId && directTopicName);

	return (
		<Modal
			open
			modalHeading="Add a new section"
			primaryButtonText="Confirm"
			primaryButtonDisabled={!canCreate}
			secondaryButtonText="Cancel"
			onRequestSubmit={() =>  // primary button close
				props.done(prepareCreateSectionData(
					docId,
					sectionType,
					(emergencyTopics?.data?.emergencies || []).find(e => e.id === emergencyId),
					directTopicId, directTopicName, emergencyTopics?.data?.others))
			}
			onRequestClose={() => props.done()}>

			<RadioButton
				id="SectionTypes.emergency" name="sectionType" className="mb"
				labelText="Emergency" value={CreateSectionType.emergency}
				checked={sectionType === CreateSectionType.emergency}
				onChange={e => {
					const v: CreateSectionType = e.target.value as CreateSectionType;
					setSectionType(v);
				}}/>
			{emergencyTopics?.data ? <ReactSelect
				className="wfp--react-select-container"
				classNamePrefix="wfp--react-select"
				value={emergencyTopics?.data?.emergencies?.find(opt => opt.id === emergencyId) || null}
				options={emergencyTopics?.data?.emergencies}
				getOptionLabel={(opt: TopicTitle) => opt.title}
				getOptionValue={(opt: TopicTitle) => '' + opt.id}
				placeholder={'Select an emergency'}
				isClearable
				onChange={(opt: any) => setEmergencyId(opt ? parseInt(opt.id) : undefined)}
			/> : <Loading withOverlay={false} active small/>
			}

			<p className="center mt mb">or</p>

			<RadioButton
				id="SectionTypes.topic" name="sectionType" className="mb"
				labelText="Topic" value={CreateSectionType.topic}
				checked={sectionType === CreateSectionType.topic}
				onChange={e => {
					const v: CreateSectionType = e.target.value as CreateSectionType;
					setSectionType(v);
				}}/>
			<ReactSelect
				id="direct-topics-select"
				className="wfp--react-select-container"
				classNamePrefix="wfp--react-select"
				value={directTopics.find(opt => opt.id === directTopicId) || null}
				options={directTopics}
				getOptionLabel={opt => opt.label}
				getOptionValue={opt => opt.id}
				placeholder={'Select a topic'}
				isClearable
				onChange={(opt: any) => setDirectTopicId(opt?.id)}
			/>
			<CreatableSelect
				// @ts-ignore
				ref={directTopicNameRef}
				id="direct-topic-create"
				className="wfp--react-select-container"
				classNamePrefix="wfp--react-select"
				maxMenuHeight={180}
				placeholder="Type a name for this section"
				isClearable
				isDisabled={!directTopicId}
				options={directTopicKnownNames}
				value={directTopicName ? {value: directTopicName, label: directTopicName} : null}
				onChange={v => setDirectTopicName(v?.value || '')}
				onBlur={event => {  // auto create option on blur
					const v = (event as React.FocusEvent<HTMLInputElement>).target.value;
					if (v) setDirectTopicName(v);
				}}
			/>

			<p className="center mt mb">or</p>

			<RadioButton
				id="SectionTypes.highlights" name="sectionType"
				labelText="Highlights" value={CreateSectionType.highlights}
				checked={sectionType === CreateSectionType.highlights}
				disabled={props.disableHighlights}
				onChange={e => {
					const v: CreateSectionType = e.target.value as CreateSectionType;
					setSectionType(v);
				}}/>
		</Modal>
	);
}